import { useMemo } from 'react';
import { useGameStore } from '../../store/gameStore';
import Cell from './Cell';

interface BoardProps {
  errorCells?: Set<number>;
}

export default function Board({ errorCells = new Set() }: BoardProps) {
  const {
    puzzle,
    currentState,
    selectedCell,
    notes,
    selectCell,
  } = useGameStore();

  // Determine grid size from puzzle length
  const gridConfig = useMemo(() => {
    const totalCells = puzzle.length;
    if (totalCells === 16) {
      return { size: 4, boxSize: 2, totalCells: 16 };
    }
    return { size: 9, boxSize: 3, totalCells: 81 };
  }, [puzzle.length]);

  const { size, boxSize, totalCells } = gridConfig;

  // Calculate related cells (same row, column, or box)
  const relatedCells = useMemo(() => {
    if (selectedCell === null) return new Set<number>();
    
    const related = new Set<number>();
    const row = Math.floor(selectedCell / size);
    const col = selectedCell % size;
    const boxRow = Math.floor(row / boxSize) * boxSize;
    const boxCol = Math.floor(col / boxSize) * boxSize;

    // Same row
    for (let c = 0; c < size; c++) {
      related.add(row * size + c);
    }

    // Same column
    for (let r = 0; r < size; r++) {
      related.add(r * size + col);
    }

    // Same box
    for (let r = boxRow; r < boxRow + boxSize; r++) {
      for (let c = boxCol; c < boxCol + boxSize; c++) {
        related.add(r * size + c);
      }
    }

    return related;
  }, [selectedCell, size, boxSize]);

  // Calculate cells with same number
  const sameNumberCells = useMemo(() => {
    if (selectedCell === null) return new Set<number>();
    
    const selectedValue = currentState[selectedCell];
    if (selectedValue === '0') return new Set<number>();

    const cells = new Set<number>();
    for (let i = 0; i < totalCells; i++) {
      if (currentState[i] === selectedValue && i !== selectedCell) {
        cells.add(i);
      }
    }
    return cells;
  }, [selectedCell, currentState, totalCells]);

  const boardClassName = size === 4 
    ? 'sudoku-board sudoku-board-4x4 w-full max-w-[320px] mx-auto'
    : 'sudoku-board w-full max-w-[480px] mx-auto';

  return (
    <div className={boardClassName}>
      {Array.from({ length: totalCells }).map((_, index) => {
        const value = parseInt(currentState[index], 10);
        const isFixed = puzzle[index] !== '0';

        return (
          <Cell
            key={index}
            index={index}
            value={value}
            isFixed={isFixed}
            isSelected={selectedCell === index}
            isRelated={relatedCells.has(index)}
            isSameNumber={sameNumberCells.has(index)}
            isError={errorCells.has(index)}
            notes={notes[index] || []}
            onClick={() => selectCell(index)}
            gridSize={size}
            boxSize={boxSize}
          />
        );
      })}
    </div>
  );
}
